import { Either } from 'src/utils/either';

import { httpResponseHandler } from 'src/http/http-response-handler';

import {
  HttpTypes,
  BadRequestError,
  ConnectionError,
  UnexpectedError,
  AccessDeniedError,
  InternalServerError,
  TooManyRequestsError,
} from '../../http';
import {
  CategoryBulkDeleteResponse,
  CategoryDeletionVerificationResponse,
  CategoryStatus,
} from '../../types/products';

export class ProductRepository {
  constructor(private api: HttpTypes.HttpClient) {}

  async getAllProducts(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product/all`,
    };

    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async getLpProducts(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product/lp-products`,
    };

    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async getLocations(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/store/location/all`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCategories(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product-category/`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getThemes(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product-theme/`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCategoriesList(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product-category/list`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCategoryDetails(
    id: string
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product-category/by-id/${id}`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async createCategory(
    category: any
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product-category/`,
      body: category,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async updateCategory(
    category: any
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product-category/update`,
      body: category,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async changeCategoryStatus(
    categoryId: string,
    status: CategoryStatus
  ): Promise<
    Either<Error, { status: 'success'; categoryId: string; categoryStatus: CategoryStatus }>
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: `benefits/product-category/${categoryId}/status`,
      body: { status },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async deleteCategories(
    categoryIds: string[]
  ): Promise<Either<Error, CategoryBulkDeleteResponse>> {
    const request: HttpTypes.HttpRequest = {
      method: 'delete',
      url: `benefits/product-category`,
      body: { categoryIds },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async verifyCategoriesDeletion(
    categoryIds: string[]
  ): Promise<Either<Error, CategoryDeletionVerificationResponse>> {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product-category/verify-deletion`,
      body: { categoryIds },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getStores(): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/store/all`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async createProduct(
    body: any
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product/create-adm`,
      body,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async createLPProduct(
    body: any
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product/lp-create-draft-product`,
      body,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async updateLPProduct(
    body: any
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: `benefits/product/lp-update-draft-product`,
      body,
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async approveLPProduct(
    id: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product/lp-approve-draft-product`,
      body: { productId: id },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async refuseLPProduct(
    id: string,
    comment: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product/lp-decline-draft-product`,
      body: { productId: id, declineComment: comment },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async changeLPProductStatus(
    id: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/product/lp-change-approved-product-status`,
      body: { productId: id },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async findById(
    id: string
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product/adm-details/${id}`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async update(
    body: any
  ): Promise<
    | Either<
        | BadRequestError
        | UnexpectedError
        | InternalServerError
        | AccessDeniedError
        | ConnectionError
        | TooManyRequestsError,
        undefined
      >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: `benefits/product/update-adm`,
      body,
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async exportProduct(
    ids: string[],
    userName: string,
    options?: { isAllSelected?: boolean }
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      // url: 'benefits/product/export-products',
      url: 'adm-reports/export-products-adm',
      body: {
        ids,
        userName,
        isAllSelected: Boolean(options?.isAllSelected),
      },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getProductsByApprovalStatus(
    params: Object
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: 'benefits/product/lp-products-by-approval-status',
      params,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getProductDetails(
    id: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/product/adm-details/${id}`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }
}
